/**
 * ArTableExportModals
 *
 * Single host for the three ArTable export modals (email, PDF export, CSV
 * import). Open state lives on the per-table Valtio proxy
 * (`uiState.exportModals`); menu items flip the flags via
 * `useArTableExportActions`, and this host subscribes only to that slice.
 *
 * Because it subscribes to nothing but `exportModals`, typing in unrelated
 * form fields never re-renders the modal subtree. Mount this once per table
 * (in `ArTableContextMenu`); both the workflow menu and the context menu
 * share the same proxy, so either can open a modal.
 */

import { FC, useContext } from 'react';
import { useSnapshot } from 'valtio';
import { ArTableUIStateContext } from '../context';
import FormEmailModal from '../../modals/FormEmailModal';
import FormExportModal from '../../pdfExport';
import { CsvImportModal } from '../import';
import type { UIArTableColumnOverride } from '#types/System.types';

type ArTableExportModalsProps = {
	columnConfig?: UIArTableColumnOverride[];
};

const ArTableExportModals: FC<ArTableExportModalsProps> = ({ columnConfig }) => {
	const uiState = useContext(ArTableUIStateContext)!;
	const modals = useSnapshot(uiState.exportModals);

	return (
		<>
			{modals.email && <FormEmailModal onCancel={() => { uiState.exportModals.email = false; }} />}
			{modals.pdf && (
				<FormExportModal
					onCancel={() => { uiState.exportModals.pdf = false; }}
					onExport={() => { uiState.exportModals.pdf = false; }}
				/>
			)}
			{modals.import && (
				<CsvImportModal
					onCancel={() => { uiState.exportModals.import = false; }}
					columnConfig={columnConfig}
				/>
			)}
		</>
	);
};

export { ArTableExportModals };
